import {useMutation, useQuery} from '@tanstack/react-query'
import './index.css'
import {QUERIES} from '../../../../../../_metronic/helpers'
import {v2_get_request_api} from '../../../../../../api/get'
import {useEffect, useMemo, useState} from 'react'
import CountdownTimer from './CountDownTimer'
import {createExamQuestionAnswer} from '../core/_requests'
import Results from './Results'
import QuizStart from './QuizStart'

const TakeExamScreen = () => {
  const [id, setId] = useState<string | null>(null)
  const [page, setPage] = useState('instructions') // instructions, quiz, results
  useEffect(() => {
    const data = sessionStorage.getItem('exam_id')
    if (data) setId(data)

    const handleContextMenu = (e: MouseEvent) => e.preventDefault()
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key.toUpperCase())) ||
        (e.ctrlKey && e.key.toUpperCase() === 'U') // view source
      ) {
        e.preventDefault()
      }
    }
    if (process.env.NODE_ENV === 'production') {
      // Request fullscreen once page loads
      const el = document.documentElement as any
      if (el.requestFullscreen) {
        el.requestFullscreen().catch((err: any) => {
          console.warn('Fullscreen request failed', err)
        })
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen()
      }
      document.addEventListener('keydown', handleKeyDown)
      document.addEventListener('contextmenu', handleContextMenu)
    }
    return () => {
      if (process.env.NODE_ENV === 'production') {
        document.removeEventListener('contextmenu', handleContextMenu)
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [])

  const {data: examData} = useQuery(
    [
      QUERIES.EXAM_ONLINE_EXAMS_LIST + '/' + id,
      `filter={"status":true,"is_published":true}&includes=ExamInstruction,Class,Subject`,
    ],
    v2_get_request_api,
    {
      enabled: !!id,
    }
  )
  const {isInitialLoading: isLoading, data: questionData} = useQuery(
    [
      QUERIES.EXAM_QUESTIONS_LIST,
      `page_size=all&order=random&filter={"status":true,"exam_to_questions.exam_online_exam_id":"${id}"}&includes=ExamAnswer`,
    ],
    v2_get_request_api,
    {
      enabled: !!id,
    }
  )
  const [userAnswers, setUserAnswers] = useState(Array(0).fill(null))
  const [takenId, setTakenId] = useState<string | null>(null)
  const quizData = useMemo(() => {
    setUserAnswers(Array(questionData?.entities?.length).fill(null))
    return questionData?.entities?.map((question: any, index: number) => ({
      ...question,
      idx: index + 1,
    }))
  }, [questionData])
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const submitAnswer = useMutation({
    mutationFn: (values: any) => createExamQuestionAnswer(values),
    onError: (error, variables) => {
      const updatedAnswers = [...userAnswers]
      updatedAnswers[variables.idx] = null
      setUserAnswers(updatedAnswers)
    },
  })
  // Select an option
  function selectOption(index: number) {
    if (timer === null) return
    submitAnswer.mutate({
      exam_online_exam_id: id,
      exam_question_id: quizData[currentQuestion].id,
      exam_answer_id: index,
      idx: currentQuestion,
    })
    const updatedAnswers = [...userAnswers]
    updatedAnswers[currentQuestion] = index
    setUserAnswers(updatedAnswers)
  }
  const submitMutation = useMutation({
    mutationFn: v2_get_request_api,
    onSuccess: () => setPage('results'),
  })

  const submit = (timeOut?: boolean) => {
    const answeredCount = userAnswers.filter((answer) => answer !== null).length
    if (!timeOut && answeredCount < quizData.length) {
      if (
        !window.confirm(
          `You have answered ${answeredCount} out of ${quizData.length} questions. Are you sure you want to submit?`
        )
      ) {
        return
      }
    }

    alert(
      `Quiz ${timeOut ? 'timed out' : 'completed'}!\nYou have answered ${answeredCount} out of ${
        quizData.length
      } questions`
    )
    setTimer(null)
    submitMutation.mutate({
      queryKey: [`${QUERIES.EXAM_ONLINE_EXAMS_LIST}/stop/${takenId}`],
    })
  }
  const [timer, setTimer] = useState<number | null>(null)
  const startQuizMutation = useMutation({
    mutationFn: () =>
      v2_get_request_api({queryKey: [`${QUERIES.EXAM_ONLINE_EXAMS_LIST}/start/${id}`]}),
    onSuccess: (res) => {
      if (!res.created) {
        alert('Test already taken.')
        return
      }
      setTakenId(res.id)
      setPage('quiz')
      setTimer(examData?.duration * 60)
    },
  })
  return (
    <div className='container my-10'>
      {page === 'results' && (
        <div className='text-center'>
          <div className='row justify-content-center'>
            <div className='col-lg-8'>
              <div
                className='card shadow-lg border-0'
                style={{borderRadius: '20px', overflow: 'hidden'}}
              >
                <div
                  className='card-header text-white py-4'
                  style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}
                >
                  <h1 className='display-4 mb-0'>{examData?.title}</h1>
                </div>
                <Results
                  result={
                    submitMutation.data
                      ? {...submitMutation.data, total_questions: quizData?.length}
                      : undefined
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {page === 'instructions' && (
        <div className='text-center'>
          <div className='row justify-content-center'>
            <QuizStart
              quiz={{
                ...examData,
                instructions: examData?.exam_instruction?.instruction,
                question_count: quizData?.length,
              }}
              onStart={() => startQuizMutation.mutate()}
              isLoading={startQuizMutation.isPending}
            />
          </div>
        </div>
      )}

      {/* Quiz Content (Initially Hidden) */}
      {page === 'quiz' && (
        <div>
          {/* Quiz Header */}
          <div className='text-center mb-4'>
            <h1 className='display-5 fw-bold text-primary'>{examData?.title}</h1>
          </div>

          {/* Question Navigator */}
          <div className='question-nav'>
            <div className='row align-items-center'>
              <div className='col-md-8'>
                <h5 className='mb-3'>
                  <i className='fas fa-list-ol'></i> Question Navigator
                </h5>
                <div id='questionNavigator' className='d-flex flex-wrap'>
                  {quizData?.map((question: any, index: number) => (
                    <div
                      key={index}
                      className={
                        'question-number ' +
                        (index === currentQuestion
                          ? 'current'
                          : userAnswers[index] !== null
                          ? 'answered'
                          : 'unanswered')
                      }
                      onClick={() => setCurrentQuestion(index)}
                    >
                      {index + 1}
                    </div>
                  ))}
                </div>
              </div>
              <div className='col-md-4'>
                <div className='quiz-stats text-center'>
                  <div className='row'>
                    <div className='col-6'>
                      <div className='fw-bold'>Answered</div>
                      <div id='answeredCount' className='h4'>
                        {userAnswers?.filter((answer) => answer !== null).length}
                      </div>
                    </div>
                    <div className='col-6'>
                      <div className='fw-bold'>Remaining</div>
                      <div id='remainingCount' className='h4'>
                        {quizData?.length - userAnswers?.filter((answer) => answer !== null).length}
                      </div>
                    </div>
                    <div className='col-12'>
                      <div className='fw-bold'>Time Remaining</div>
                      <div id='remainingCount' className='h4'>
                        {timer && (
                          <CountdownTimer initialSeconds={timer} action={() => submit(true)} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className='quiz-progress'>
            <div className='progress' style={{height: '8px', borderRadius: '10px'}}>
              <div
                id='progressBar'
                className='progress-bar bg-success progress-bar-striped progress-bar-animated'
                role='progressbar'
                style={{width: ((currentQuestion + 1) / (quizData?.length || 1)) * 100 + '%'}}
              ></div>
            </div>
            <div className='text-center mt-2 small text-muted'>
              <span id='progressText'>
                Question {currentQuestion + 1} of {quizData?.length || 1}
              </span>
            </div>
          </div>

          {/* Question Card */}
          <div className='question-card card'>
            <div className='question-header'>
              <div className='d-flex align-items-center'>
                <i className='fas fa-question-circle me-2'></i>
                <span>
                  Question <span id='currentQuestionNumber'>{currentQuestion + 1}</span>
                </span>
              </div>
            </div>
            <div className='card-body p-4'>
              <h4
                id='questionText'
                className='mb-4 ck-content'
                style={{minHeight: '50px'}}
                dangerouslySetInnerHTML={{
                  __html: isLoading
                    ? 'Loading question...'
                    : quizData
                    ? quizData[currentQuestion]?.question
                    : '',
                }}
              ></h4>
              <div id='optionsContainer'>
                {quizData?.[currentQuestion]?.exam_answers.map((option: any, index: number) => (
                  <div
                    className={`option-item p-3 ${
                      userAnswers[currentQuestion] === option.id ? 'selected' : ''
                    }`}
                    key={index}
                    onClick={() => selectOption(option.id)}
                  >
                    <div className='d-flex align-items-center'>
                      <div className='option-letter'>{String.fromCharCode(65 + index)}</div>
                      <div className='option-text'>{option.option}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className='nav-buttons d-flex justify-content-between'>
            <button
              id='prevBtn'
              className='btn btn-outline-primary btn-lg'
              disabled={currentQuestion === 0}
              onClick={() => {
                if (currentQuestion > 0) {
                  setCurrentQuestion(currentQuestion - 1)
                }
              }}
            >
              <i className='fas fa-chevron-left'></i> Previous
            </button>
            <div className='d-flex gap-3'>
              <button
                id='submitBtn'
                className={
                  'btn btn-success btn-lg ' +
                  (currentQuestion !== quizData?.length - 1 ? 'd-none' : 'inline-block')
                }
                onClick={() => submit()}
              >
                <i className='fas fa-paper-plane'></i> Submit Quiz
              </button>
              <button
                id='nextBtn'
                className={
                  'btn btn-primary btn-lg ' +
                  (currentQuestion === quizData?.length - 1 ? 'd-none' : 'inline-block')
                }
                onClick={() => {
                  if (currentQuestion < quizData?.length - 1) {
                    setCurrentQuestion(currentQuestion + 1)
                  }
                }}
              >
                Next <i className='fas fa-chevron-right'></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
export default TakeExamScreen
